import supabase from '@/shared/libs/supabase';

export type PdfExportScope = 'course' | 'unit' | 'lesson';

function parseFilenameFromDisposition(disposition?: string | null): string | null {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));

  const plainMatch = disposition.match(/filename=([^;]+)/i);
  if (!plainMatch?.[1]) return null;
  return plainMatch[1].trim().replace(/^"|"$/g, '');
}

function normalizeBlob(data: unknown): Blob {
  if (data instanceof Blob) return data;
  if (data instanceof ArrayBuffer) return new Blob([data], { type: 'application/pdf' });
  if (data instanceof Uint8Array) {
    const copied = new Uint8Array(data.byteLength);
    copied.set(data);
    return new Blob([copied.buffer], { type: 'application/pdf' });
  }
  if (typeof data === 'string') return new Blob([data], { type: 'application/pdf' });

  return new Blob([JSON.stringify(data ?? {})], { type: 'application/pdf' });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function getFunctionsUrl(): string {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!baseUrl) throw new Error('Missing VITE_SUPABASE_URL');
  return `${baseUrl.replace(/\/$/, '')}/functions/v1`;
}

export async function exportCurriculumPdf(
  scopeType: PdfExportScope,
  scopeId: string,
  fallbackFilename: string
): Promise<void> {
  const { data: auth } = await supabase.auth.getSession();
  const accessToken = auth.session?.access_token;
  if (!accessToken) throw new Error('You must be signed in to export PDF');

  const response = await fetch(`${getFunctionsUrl()}/curriculum-pdf-export`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ scopeType, scopeId }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    try {
      const parsed = JSON.parse(responseText) as { error?: string };
      throw new Error(parsed.error || `PDF export failed (${response.status})`);
    } catch {
      throw new Error(responseText || `PDF export failed (${response.status})`);
    }
  }

  const blob = await response.blob();
  const normalizedBlob = normalizeBlob(blob);
  const disposition = response.headers.get('content-disposition');
  const parsedFilename = parseFilenameFromDisposition(disposition);
  const filename = parsedFilename || fallbackFilename;

  triggerDownload(normalizedBlob, filename);
}
